/**
 * Générateur — Attendu 951 — Définir le demi-tour, ou symétrie centrale.
 * Niveau : 5ème | Domaine : Espace et géométrie / Transformations
 *
 * Plan de cartes (version alignée sur la spécification 951 du 2026-09-21) :
 *
 *   Carte « Définir le demi-tour » — le cœur de l'attendu, dont le verbe est
 *     DÉFINIR et non construire. Six formats, un par niveau de la graduation :
 *     compléter la définition de référence, la choisir parmi des énoncés
 *     voisins, la distinguer d'une propriété vraie, énoncer soi-même ce qui
 *     caractérise le centre, réfuter une définition candidate, mélange.
 *   Carte « Où est le centre de symétrie ? » — caractérisation du centre sur
 *     une figure.
 *   Carte « Figures à centre de symétrie » — chiffres et lettres, avec toujours
 *     la réponse « aucun » offerte.
 *   Cartes « Construire le symétrique », « Coordonnées du symétrique » et
 *     « Image d'une figure sur quadrillage » — la construction, gardée comme
 *     VÉRIFICATION de la définition, jamais comme substitut.
 *
 * ── Spécification 951 ──────────────────────────────────────────────────────
 *
 * Bornes appliquées :
 *   enonce_de_reference_de_la_definition : DEFINITION, employée mot pour mot
 *     dans tous les énoncés, tous les choix corrects et tous les rappels
 *   nombre_de_definitions_concurrentes_proposees 3 à 5 → 4
 *   nombre_de_proprietes_presentees_comme_definitions 1 à 2 → 1 au niveau 2,
 *     2 au niveau 3
 *   coordonnees_du_centre -6 à 6
 *   distance_du_centre_a_chaque_point 2 à 8 (distance euclidienne)
 *   nombre_de_points_de_la_figure 1 à 4
 *   angle_du_demi_tour 180 (écrit UNE fois, dans l'exposé qui relie les deux noms)
 *   ecart_angulaire_des_rotations_voisines_proposees 10 à 100
 *   nombre_de_contre_exemples_a_examiner 1 à 3
 *
 * Exclusions appliquées : aucun point à moins de deux unités du centre ni
 * confondu avec lui ; aucune figure invariante par le demi-tour dans les tâches
 * de définition ; une rotation de 180 degrés n'est jamais nommée autrement que
 * demi-tour ou symétrie centrale ; aucun distracteur n'est une affirmation
 * FAUSSE sur le demi-tour (chacun est soit la définition d'une autre
 * transformation, soit une propriété vraie non caractéristique) ; la symétrie
 * axiale n'apparaît qu'en contraste ; aucune composition de transformations.
 *
 * Erreurs visées → mots-clés :
 *   Prendre une propriété pour la définition        → propriete_pour_definition
 *   Définir par le retournement apparent            → retournement_apparent
 *   Confondre centre de symétrie et axe de symétrie → centre_pour_axe
 *   Croire que le centre appartient à la figure     → centre_dans_la_figure
 *   Oublier que le centre est sa propre image       → centre_sans_image
 *   Croire qu'une rotation quelconque est un demi-tour → rotation_quelconque
 *   Ne pas voir que le demi-tour est sa propre réciproque → reciproque_ignoree
 *   Énoncer la définition pour une figure, non pour un point → definition_pour_la_figure
 *
 * Graduation → cartes :
 *   niveau 1 (compléter)  definition_texte_trou_drag
 *   niveau 2 (choisir)    definition_qcm_unique
 *   niveau 3 (propriété)  definition_propriete_qcm_unique
 *   niveau 4 (énoncer)    definition_caracteriser_saisie, centre_qcm_unique
 *   niveau 5 (réfuter)    definition_refuter_qcm_unique
 *   niveau 6 (mélange)    definition_melange_qcm_unique
 *   réglage : les deux noms sont employés en alternance
 *
 * Les deux QCM de définition (niveaux 2 et 3) se ressemblent : même vivier de
 * onze énoncés, même bonne réponse. Question posée et TRANCHÉE le 2026-09-26 :
 * on les garde tous les deux, et la borne de la spec reste à 2 propriétés. Les
 * séparer franchement demanderait de ne mettre QUE des propriétés au niveau 3,
 * ce que la borne interdit. Et supprimer le niveau 2 ferait perdre le contraste
 * avec la symétrie axiale — garanti dans 100 % de ses tirages, contre 1 sur 5
 * ailleurs — donc l'erreur visée centre_pour_axe n'aurait plus de carte.
 *
 * Les figures sont construites par 2C − A EXACT (symCentral) ; aucune tolérance
 * n'entre dans leur fabrication.
 */

import {
    randInt, randomChoice, shuffle,
    makeQcm, makeSaisie, makeTexteTrou, basePayload, filtrerDistracteurs,
} from '../../../utils';
import {
    elementGrille, symCentral, fr, paire, BLEU, ORANGE,
} from './svgTransfo';

const ATTENDU_ID = 951;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_DEF = 'Définir le demi-tour';
const L_CONSTRUIRE = 'Construire le symétrique d\'un point';
const L_COORD = 'Coordonnées du symétrique';
const L_CENTRE = 'Où est le centre de symétrie ?';
const L_IMAGE = 'Image d\'une figure sur quadrillage';
const L_CARACTERES = 'Figures à centre de symétrie';

const CONSIGNE = '(Écris chaque nombre sans le signe + : 3 ou -2.)';
const dansGrille = (p, b) => p[0] >= -b && p[0] <= b && p[1] >= -b && p[1] <= b;
/** Exclusion 1 : jamais confondu avec le centre, jamais à moins de deux unités. */
const distanceOk = (p, c) => {
    const d = Math.hypot(p[0] - c[0], p[1] - c[1]);
    return d >= 2 && d <= 8;
};

// ═══════════════════════════════════════════════════════════
// LA DÉFINITION DE RÉFÉRENCE — employée mot pour mot partout (vigilance 2)
// ═══════════════════════════════════════════════════════════

const DEFINITION = 'Le demi-tour de centre O, aussi appelé symétrie centrale de centre O, '
    + 'est la transformation qui laisse O fixe et qui, à tout autre point M, associe le point M\' '
    + 'tel que O soit le milieu du segment [MM\'].';
/** Le seul endroit du dispositif où la mesure de l'angle est écrite (écart assumé 4). */
const EXPOSE_DEUX_NOMS = 'Un tour complet mesure 360°, un demi-tour en mesure donc 180°. '
    + 'On dit aussi bien « demi-tour de centre O » que « symétrie centrale de centre O » : '
    + 'ce sont deux noms de la même transformation.';

// ═══════════════════════════════════════════════════════════
// Carte « Définir le demi-tour »
// ═══════════════════════════════════════════════════════════

// niveau 1 : compléter la définition à l'endroit qui la fait.
const MOTS_LEURRES = ['symétrique', 'extrémité', 'sommet', 'double', 'quart', 'prolongement'];

const genDefinitionTrou = () => {
    const phrase = DEFINITION.replace('le milieu du segment', 'le {{milieu}} du segment');
    const leurres = shuffle(MOTS_LEURRES).slice(0, 3);
    const payload = makeTexteTrou(phrase,
        '<p><b>Complète la définition du demi-tour (ou symétrie centrale).</b></p>'
        + `<p><i>${EXPOSE_DEUX_NOMS}</i></p>`, {
            ...opts(L_DEF), mode: 'drag',
            distracteurs: filtrerDistracteurs(phrase, leurres),
        });
    payload._meta.params = {
        niveau: 'n1', definition: DEFINITION,
        distracteurs: leurres.map((m) => ({ code: 'propriete_pour_definition', valeur: m })),
        erreursVisees: ['definition_pour_la_figure'],
    };
    return payload;
};

// Énoncés voisins : JAMAIS une affirmation fausse sur le demi-tour (exclusion 4).
// Chacun est soit la définition d'une AUTRE transformation, soit une propriété
// VRAIE du demi-tour qui ne le caractérise pas.
const AUTRES_DEFINITIONS = [
    {
        code: 'centre_pour_axe',
        valeur: 'La symétrie d’axe (d) est la transformation qui, à tout point M, associe le point M\' '
            + 'tel que (d) soit la médiatrice du segment [MM\'].',
    },
    {
        code: 'rotation_quelconque',
        valeur: 'C’est la transformation qui fait tourner la figure autour d’un point, d’un angle quelconque.',
    },
    {
        code: 'retournement_apparent',
        valeur: 'C’est la transformation qui retourne la figure, comme si on la regardait dans un miroir.',
    },
    {
        code: 'definition_pour_la_figure',
        valeur: 'C’est la transformation qui retourne la figure entière d’un seul bloc, sans la déformer.',
    },
    {
        code: 'centre_dans_la_figure',
        valeur: 'C’est la transformation qui fait tourner la figure autour de son milieu.',
    },
];

const PROPRIETES_VRAIES = [
    { code: 'propriete_pour_definition', valeur: 'C’est la transformation qui conserve les longueurs.' },
    { code: 'propriete_pour_definition', valeur: 'C’est la transformation qui transforme une droite en une droite qui lui est parallèle.' },
    { code: 'propriete_pour_definition', valeur: 'C’est la transformation qui conserve les aires et les mesures d’angles.' },
    { code: 'reciproque_ignoree', valeur: 'C’est la transformation qui, appliquée deux fois, redonne la figure de départ.' },
    { code: 'centre_sans_image', valeur: 'C’est la transformation qui possède un point fixe et un seul.' },
];

/**
 * QCM de la définition : quatre propositions, la définition de référence et
 * trois énoncés voisins dont `nbProprietes` propriétés vraies mais non
 * caractéristiques (`nombre_de_proprietes_presentees_comme_definitions`).
 */
/**
 * Les deux QCM de définition.
 *
 * Les deux formats tiraient dans le MÊME sac avec le MÊME énoncé, mot pour mot :
 * sur 2 000 tirages, les 11 énoncés servis étaient communs aux deux et chaque
 * tirage de l'un était formable par l'autre. Deux cartes pour un seul exercice
 * (retour prof 2026-09-26).
 *
 * Deux différences RÉELLES les séparent maintenant :
 *   - niveau 2 : la définition de la symétrie axiale est TOUJOURS présente, comme
 *     la graduation l'exige (« parmi des énoncés voisins, dont la définition de la
 *     symétrie axiale ») ; elle était jusqu'ici tirée au sort et manquait souvent.
 *   - l'énoncé annonce la nature des distracteurs, et elle diffère : un voisin et
 *     une propriété au niveau 2, deux propriétés au niveau 3. L'élève sait donc au
 *     niveau 3 qu'il doit trancher sur le STATUT des énoncés, pas sur leur objet.
 *
 * La borne `nombre_de_proprietes_presentees_comme_definitions` de la spec (1 à 2,
 * « au moins un distracteur est une propriété vraie ») interdit d'aller plus loin,
 * c'est-à-dire de ne mettre QUE des propriétés au niveau 3. Le vivier reste commun.
 */
const construireDefinitionQcm = (label, niveau, variante) => {
    const proprietes = variante === 'proprietes';
    // La spec borne `nombre_de_proprietes_presentees_comme_definitions` a 1 ou 2 :
    // AU MOINS une propriete vraie partout, jamais plus de deux. On reste dedans.
    const nbProp = proprietes ? 2 : 1;
    const props = shuffle(PROPRIETES_VRAIES).slice(0, nbProp);
    // La definition de la symetrie axiale est le contraste explicite que la
    // graduation exige au niveau 2 (« dont la definition de la symetrie axiale ») :
    // elle y est desormais TOUJOURS presente, au lieu d'etre tiree au sort.
    const axiale = AUTRES_DEFINITIONS.find((d) => d.code === 'centre_pour_axe');
    const autres = proprietes
        ? shuffle(AUTRES_DEFINITIONS).slice(0, 1)
        : [axiale, ...shuffle(AUTRES_DEFINITIONS.filter((d) => d !== axiale)).slice(0, 1)];
    const distracteurs = shuffle([...props, ...autres]);
    const texte = proprietes
        ? '<p><b>Parmi ces énoncés, lequel DÉFINIT le demi-tour (ou symétrie centrale) ?</b></p>'
            + '<p><i>Attention : <b>deux</b> de ces énoncés sont des propriétés vraies du '
            + 'demi-tour. Vraies, mais elles ne le définissent pas, parce que d’autres '
            + 'transformations les vérifient aussi.</i></p>'
        : '<p><b>Parmi ces énoncés, lequel DÉFINIT le demi-tour (ou symétrie centrale) ?</b></p>'
            + '<p><i>Les autres ne sont pas faux : l’un définit une <b>autre</b> '
            + 'transformation, un autre est une propriété vraie du demi-tour qui ne suffit '
            + 'pas à le définir.</i></p>';
    const payload = makeQcm([DEFINITION, ...distracteurs.map((d) => d.valeur)], DEFINITION,
        texte, opts(label));
    payload._meta.params = {
        niveau, definition: DEFINITION, variante, distracteurs,
        erreursVisees: [proprietes ? 'propriete_pour_definition' : 'centre_pour_axe'],
    };
    return payload;
};

const genDefinitionQcm = () => construireDefinitionQcm(L_DEF, 'n2', 'transformations');
const genDefinitionProprieteQcm = () => construireDefinitionQcm(L_DEF, 'n3', 'proprietes');

// niveau 4 : énoncer soi-même ce qui caractérise le centre.
const NOMS_CARACT = ['A', 'B', 'E', 'F', 'K', 'M', 'N', 'P', 'R', 'S', 'T'];

/** Les formulations admises pour « O est le milieu de [AA'] » (vigilance 3). */
const formulationsMilieu = (nom, centre) => {
    const apostrophes = ['\'', '’'];
    const out = [];
    apostrophes.forEach((ap) => {
        const image = `${nom}${ap}`;
        const paires = [`[${nom}${image}]`, `${nom}${image}`, `[${image}${nom}]`, `${image}${nom}`];
        paires.forEach((seg) => {
            [`${centre} est le milieu de ${seg}`,
                `${centre} est le milieu du segment ${seg}`,
                `le point ${centre} est le milieu de ${seg}`,
                `le point ${centre} est le milieu du segment ${seg}`,
                `milieu de ${seg}`,
                `milieu du segment ${seg}`,
            ].forEach((p) => { out.push(p); out.push(`${p}.`); });
        });
    });
    return [...new Set(out)];
};

const genCaracteriserSaisie = () => {
    const b = 6;
    let C, A, Ap;
    for (let essai = 0; essai < 200; essai++) {
        C = [randInt(-3, 3), randInt(-3, 3)];
        A = [randInt(-b, b), randInt(-b, b)];
        Ap = symCentral(A, C);
        if (distanceOk(A, C) && dansGrille(Ap, b)) break;
    }
    const nom = randomChoice(NOMS_CARACT);
    const image = `${nom}'`;
    const texte = `<p>Sur la figure, le point ${image} est l'image du point ${nom} par le demi-tour `
        + `de centre O. <b>Écris en une phrase ce qui relie le point O aux points ${nom} et ${image}.</b></p>`
        + '<p><i>Exemple de réponse attendue : « … est le milieu du segment … ».</i></p>';
    const payload = makeSaisie(`O est le milieu du segment [${nom}${image}]`, texte, {
        ...opts(L_DEF), suffixe: '',
        comparaison: 'expression', reponses_acceptees: formulationsMilieu(nom, 'O'),
        elements_additionnels: elementGrille({
            xMin: -b, yMin: -b, xMax: b, yMax: b, axes: true,
            points: [
                { x: A[0], y: A[1], label: nom, couleur: BLEU },
                { x: Ap[0], y: Ap[1], label: image, couleur: ORANGE },
            ],
            centres: [{ x: C[0], y: C[1], label: 'O' }],
            segments: [{ a: A, b: Ap, couleur: '#94a3b8', dash: true }],
        }),
    });
    payload._meta.params = {
        niveau: 'n4', definition: DEFINITION, centre: C, point: A, image: Ap, nom,
        erreursVisees: ['definition_pour_la_figure', 'centre_dans_la_figure'],
    };
    return payload;
};

// niveau 5 : réfuter une définition candidate par un contre-exemple.
const candidatsARefuter = (angle) => [
    {
        candidate: 'Le demi-tour est la transformation qui conserve les longueurs.',
        correct: 'La symétrie d’axe (d) conserve aussi les longueurs, et ce n’est pas un demi-tour.',
        distracteurs: [
            { code: 'propriete_pour_definition', valeur: 'Un demi-tour conserve bien les longueurs : la phrase suffit donc à le définir.' },
            { code: 'centre_sans_image', valeur: 'Le centre O est son propre image.' },
            { code: 'reciproque_ignoree', valeur: 'Appliqué deux fois, le demi-tour redonne la figure de départ.' },
        ],
    },
    {
        candidate: 'Le demi-tour est la transformation qui fait tourner la figure autour d’un point.',
        correct: `La rotation de ${angle}° autour de O fait aussi tourner la figure autour d’un point, et ce n’est pas un demi-tour.`,
        distracteurs: [
            { code: 'rotation_quelconque', valeur: 'Un demi-tour fait bien tourner la figure autour d’un point : la phrase suffit donc à le définir.' },
            { code: 'propriete_pour_definition', valeur: 'Un demi-tour conserve les longueurs.' },
            { code: 'centre_dans_la_figure', valeur: 'Le centre du demi-tour est toujours à l’intérieur de la figure.' },
        ],
    },
    {
        candidate: 'Le demi-tour est la transformation qui transforme une droite en une droite parallèle.',
        correct: 'La symétrie d’axe (d) transforme aussi certaines droites en droites parallèles, et ce n’est pas un demi-tour.',
        distracteurs: [
            { code: 'propriete_pour_definition', valeur: 'Un demi-tour transforme bien une droite en une droite parallèle : la phrase suffit donc à le définir.' },
            { code: 'reciproque_ignoree', valeur: 'Le demi-tour est sa propre réciproque.' },
            { code: 'centre_sans_image', valeur: 'Le centre est le seul point qui reste à sa place.' },
        ],
    },
];

const genRefuterQcm = () => {
    // ecart_angulaire_des_rotations_voisines_proposees : de 10 à 100 degrés
    const angle = 180 - randInt(10, 100);
    const c = randomChoice(candidatsARefuter(angle));
    const texte = `<p>Un élève propose cette définition : <b>« ${c.candidate} »</b></p>`
        + '<p><b>Quel exemple montre que cette phrase ne suffit PAS à définir le demi-tour ?</b></p>'
        + `<p><i>Rappel de la définition : ${DEFINITION}</i></p>`;
    const payload = makeQcm([c.correct, ...c.distracteurs.map((d) => d.valeur)], c.correct,
        texte, opts(L_DEF));
    payload._meta.params = {
        niveau: 'n5', definition: DEFINITION, angleRotation: angle, contreExemples: 1,
        distracteurs: c.distracteurs, erreursVisees: ['propriete_pour_definition'],
    };
    return payload;
};

const genDefinitionMelange = () => {
    const p = randomChoice([
        () => construireDefinitionQcm(L_DEF, 'n6', 'transformations'),
        () => construireDefinitionQcm(L_DEF, 'n6', 'proprietes'),
        genRefuterQcm,
    ])();
    p._meta.params.niveau = 'n6';
    return p;
};

// ═══════════════════════════════════════════════════════════
// Carte « Construire le symétrique d'un point » (vérification)
// ═══════════════════════════════════════════════════════════

const genConstruirePlacer = () => {
    const b = 5;
    let C, A, Bp, D, E;
    for (let essai = 0; essai < 300; essai++) {
        C = [randInt(-2, 2), randInt(-2, 2)];
        A = [randInt(-b, b), randInt(-b, b)];
        Bp = [randInt(-b, b), randInt(-b, b)];
        D = symCentral(A, C);
        E = symCentral(Bp, C);
        const cles = new Set([C, A, Bp, D, E].map((p) => p.join(',')));
        if (cles.size === 5 && dansGrille(D, b) && dansGrille(E, b)
            && distanceOk(A, C) && distanceOk(Bp, C)) break;
    }
    const bonne = { D: `${fr(D[0])};${fr(D[1])}`, E: `${fr(E[0])};${fr(E[1])}` };
    const payload = basePayload('association',
        '<p><b>C est le centre du demi-tour (symétrie centrale).</b> Place le point D, image de A, '
        + 'et le point E, image de B.</p>'
        + `<p><i>Rappel : ${DEFINITION}</i></p>`,
        {
            subType: 'placer_points_2d',
            repere: { xMin: -b, yMin: -b, xMax: b, yMax: b, xStep: 1, yStep: 1 },
            pointsToPlace: ['D', 'E'],
            dejaPlaces: [
                { x: C[0], y: C[1], label: 'C' },
                { x: A[0], y: A[1], label: 'A' },
                { x: Bp[0], y: Bp[1], label: 'B' },
            ],
            bonne_reponse: JSON.stringify(bonne),
        },
        opts(L_CONSTRUIRE));
    payload._meta.params = {
        niveau: 'verification', centre: C, points: [A, Bp], images: [D, E],
        erreursVisees: ['centre_sans_image'],
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte « Coordonnées du symétrique » (vérification)
// ═══════════════════════════════════════════════════════════

const genCoordSaisie = () => {
    const b = 6;
    let C, A, D;
    for (let essai = 0; essai < 300; essai++) {
        C = [randInt(-3, 3), randInt(-3, 3)];
        A = [randInt(-b, b), randInt(-b, b)];
        D = symCentral(A, C);
        if (dansGrille(D, b) && distanceOk(A, C)) break;
    }
    const phrase = 'Le point D est l\'image de A par le demi-tour de centre C. '
        + `Son abscisse est {{${fr(D[0])}}} et son ordonnée est {{${fr(D[1])}}}.`;
    const payload = makeTexteTrou(phrase,
        `<p><b>A${paire(A[0], A[1])} et le centre C${paire(C[0], C[1])} sont placés dans le repère. `
        + `Donne les coordonnées de D.</b> ${CONSIGNE}</p>`
        + `<p><i>Rappel : ${DEFINITION}</i></p>`,
        {
            ...opts(L_COORD), mode: 'saisie',
            elements_additionnels: elementGrille({
                xMin: -b, yMin: -b, xMax: b, yMax: b, axes: true,
                points: [{ x: A[0], y: A[1], label: 'A', couleur: BLEU }],
                centres: [{ x: C[0], y: C[1], label: 'C' }],
            }),
        });
    payload._meta.params = {
        niveau: 'verification', centre: C, points: [A], images: [D],
        erreursVisees: ['centre_sans_image'],
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte « Où est le centre de symétrie ? » (caractérisation)
// ═══════════════════════════════════════════════════════════

const DIRS = [[1, 0], [0, 1], [1, 1], [1, -1]];
const LETTRES_CROIX = ['m', 'n', 'p', 'r', 's'];

const genCentreQcm = () => {
    const b = 5;
    let M, d, k, A, B, surSeg, hors;
    for (let essai = 0; essai < 300; essai++) {
        M = [randInt(-2, 2), randInt(-2, 2)];
        d = randomChoice(DIRS);
        k = randInt(2, 3);
        A = [M[0] - k * d[0], M[1] - k * d[1]];
        B = [M[0] + k * d[0], M[1] + k * d[1]];
        surSeg = [M[0] - d[0], M[1] - d[1]];              // sur [AB], mais pas le milieu
        const perp = [-d[1], d[0]];
        hors = [M[0] + perp[0], M[1] + perp[1]];          // hors de la droite (AB)
        if (dansGrille(A, b) && dansGrille(B, b) && dansGrille(hors, b)
            && distanceOk(A, M) && distanceOk(B, M)
            && surSeg.join() !== M.join() && hors.join() !== M.join()) break;
    }
    const positions = shuffle([
        { x: M[0], y: M[1], code: null },
        { x: surSeg[0], y: surSeg[1], code: 'centre_dans_la_figure' },
        { x: hors[0], y: hors[1], code: 'centre_pour_axe' },
    ]);
    const lettres = shuffle(LETTRES_CROIX).slice(0, 3);
    const croix = positions.map((p, i) => ({ x: p.x, y: p.y, label: lettres[i], code: p.code }));
    const correct = croix.find((c) => c.code === null).label;
    const payload = makeQcm(croix.map((c) => c.label), correct,
        '<p><b>Le point B est l\'image du point A par un demi-tour. '
        + 'Lequel des points marqués est le centre de ce demi-tour ?</b></p>'
        + `<p><i>Rappel : ${DEFINITION}</i></p>`,
        {
            ...opts(L_CENTRE),
            elements_additionnels: elementGrille({
                xMin: -b, yMin: -b, xMax: b, yMax: b,
                points: [{ x: A[0], y: A[1], label: 'A', couleur: BLEU }, { x: B[0], y: B[1], label: 'B', couleur: ORANGE }],
                croix,
            }),
        });
    payload._meta.params = {
        niveau: 'n4', centre: M, points: [A], images: [B],
        distracteurs: croix.filter((c) => c.code).map((c) => ({ code: c.code, valeur: c.label })),
        erreursVisees: [],
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte « Figures à centre de symétrie »
// ═══════════════════════════════════════════════════════════

// Caractères inchangés par un demi-tour (ils ont un centre de symétrie).
const A_CENTRE = ['0', '8', 'H', 'I', 'N', 'O', 'S', 'X', 'Z'];
// Caractères qui ont un AXE de symétrie mais AUCUN centre : c'est la confusion
// la plus fréquente sur les lettres et les chiffres (erreur visée 3).
const AXE_SEULEMENT = ['3', 'A', 'B', 'C', 'D', 'E', 'M', 'T', 'U', 'V', 'W', 'Y'];
const AUCUN = 'Aucun de ces caractères.';

/** « la lettre H », « le chiffre 8 » : jamais une lettre nue, qui serait relue comme un index. */
const nommer = (c) => (/[0-9]/.test(c) ? `le chiffre ${c}` : `la lettre ${c}`);

const genCaracteresQcm = () => {
    const avecReponse = randomChoice([true, true, false]);
    const leurres = shuffle(AXE_SEULEMENT).slice(0, avecReponse ? 3 : 4);
    const bon = avecReponse ? randomChoice(A_CENTRE) : null;
    const caracteres = shuffle(avecReponse ? [bon, ...leurres] : leurres).map(nommer);
    const correct = avecReponse ? nommer(bon) : AUCUN;
    const payload = makeQcm([...caracteres, AUCUN], correct,
        '<p><b>Parmi ces caractères, lequel est inchangé par un demi-tour, '
        + 'c\'est-à-dire lequel a un centre de symétrie ?</b></p>'
        + '<p><i>Attention : avoir un axe de symétrie n\'est pas la même chose '
        + 'qu\'avoir un centre de symétrie.</i></p>',
        opts(L_CARACTERES));
    payload._meta.params = {
        niveau: 'reconnaissance', avecReponse,
        distracteurs: leurres.map((c) => ({ code: 'centre_pour_axe', valeur: nommer(c) })),
        erreursVisees: ['centre_pour_axe'],
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte « Image d'une figure sur quadrillage » (vérification)
// ═══════════════════════════════════════════════════════════

const GN = 5;
const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXY';
const lettreDe = (x, y) => ALPHABET[(GN - 1 - y) * GN + x];

const genImageGrilleQcm = () => {
    let O, P, Q, Pp, Qp;
    for (let essai = 0; essai < 400; essai++) {
        O = [randInt(1, 3), randInt(1, 3)];
        P = [randInt(0, 4), randInt(0, 4)];
        Q = [randInt(0, 4), randInt(0, 4)];
        Pp = symCentral(P, O);
        Qp = symCentral(Q, O);
        const pts = [O, P, Q, Pp, Qp];
        const dedans = pts.every((p) => p[0] >= 0 && p[0] <= 4 && p[1] >= 0 && p[1] <= 4);
        const distincts = new Set(pts.map((p) => p.join())).size === 5;
        // exclusion 2 : le segment ne doit pas être invariant (O milieu de [PQ])
        const invariant = (P[0] + Q[0] === 2 * O[0]) && (P[1] + Q[1] === 2 * O[1]);
        if (dedans && distincts && !invariant && distanceOk(P, O) && distanceOk(Q, O)) break;
    }
    const nomSeg = (a, bp) => `[${lettreDe(...a)}${lettreDe(...bp)}]`;
    const correct = nomSeg(Pp, Qp);
    const reverse = `[${lettreDe(...Qp)}${lettreDe(...Pp)}]`;
    const candidats = [
        { code: 'centre_sans_image', valeur: nomSeg(P, Q) },            // oubli de transformer
        { code: 'definition_pour_la_figure', valeur: nomSeg(Pp, Q) },   // un seul sommet transformé
        { code: 'definition_pour_la_figure', valeur: nomSeg(P, Qp) },   // l'autre sommet seulement
    ];
    const distracteurs = [];
    candidats.forEach((c) => {
        if (distracteurs.length < 3 && c.valeur !== correct && c.valeur !== reverse
            && !distracteurs.some((d) => d.valeur === c.valeur)) distracteurs.push(c);
    });
    if (distracteurs.length < 3) return genImageGrilleQcm();      // tirage dégénéré, rejoué
    const points = [];
    for (let y = 4; y >= 0; y--) for (let x = 0; x <= 4; x++) points.push({ x, y, label: lettreDe(x, y), couleur: '#64748b' });
    const payload = makeQcm([correct, ...distracteurs.map((d) => d.valeur)], correct,
        '<p><b>Sur le quadrillage, chaque nœud est repéré par une lettre.</b> '
        + `Quelle est l'image du segment [${lettreDe(...P)}${lettreDe(...Q)}] par le demi-tour `
        + `de centre ${lettreDe(...O)} ?</p>`
        + `<p><i>Rappel : ${DEFINITION}</i></p>`,
        {
            ...opts(L_IMAGE),
            elements_additionnels: elementGrille({
                xMin: 0, yMin: 0, xMax: 4, yMax: 4,
                points,
                segments: [{ a: P, b: Q, couleur: BLEU }],
                centres: [{ x: O[0], y: O[1], label: '' }],
            }),
        });
    payload._meta.params = {
        niveau: 'verification', centre: O, points: [P, Q], images: [Pp, Qp], distracteurs,
        erreursVisees: [],
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// REGISTRE
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    definition_texte_trou_drag: genDefinitionTrou,
    definition_qcm_unique: genDefinitionQcm,
    definition_propriete_qcm_unique: genDefinitionProprieteQcm,
    definition_caracteriser_saisie: genCaracteriserSaisie,
    definition_refuter_qcm_unique: genRefuterQcm,
    definition_melange_qcm_unique: genDefinitionMelange,
    construire_placer_points_2d: genConstruirePlacer,
    coord_texte_trou_saisie: genCoordSaisie,
    centre_qcm_unique: genCentreQcm,
    caracteres_qcm_unique: genCaracteresQcm,
    image_grille_qcm_unique: genImageGrilleQcm,
};

const FORMAT_LABELS = {
    placer_points_2d: 'Placer (interactif)', texte_trou_saisie: 'Saisie trous',
    texte_trou_drag: 'Compléter la définition', qcm_unique: 'QCM',
    propriete_qcm_unique: 'QCM définition ou propriété',
    caracteriser_saisie: 'Énoncer la caractérisation',
    refuter_qcm_unique: 'QCM réfuter', melange_qcm_unique: 'QCM mélange',
    grille_qcm_unique: 'QCM',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'definition', label: L_DEF, formatsList: buildFormats('definition') },
    { id: 'centre', label: L_CENTRE, formatsList: buildFormats('centre') },
    { id: 'caracteres', label: L_CARACTERES, formatsList: buildFormats('caracteres') },
    { id: 'construire', label: L_CONSTRUIRE, formatsList: buildFormats('construire') },
    { id: 'coord', label: L_COORD, formatsList: buildFormats('coord') },
    { id: 'image', label: L_IMAGE, formatsList: buildFormats('image') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = { DEFINITION, formulationsMilieu, distanceOk, symCentral };
